import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowRight } from "lucide-react";
import { scrollToHash } from "./SmoothScroll";

const LINKS = [
  { label: "Come funziona", href: "#come-funziona", testid: "nav-link-come-funziona" },
  { label: "Il servizio", href: "#servizio", testid: "nav-link-servizio" },
  { label: "Il professionista", href: "#professionista", testid: "nav-link-professionista" },
  { label: "Per chi è", href: "#per-chi", testid: "nav-link-per-chi" },
  { label: "FAQ", href: "#faq", testid: "nav-link-faq" },
  { label: "Contatti", href: "#contatti", testid: "nav-link-contatti" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (e, href) => {
    e.preventDefault();
    setOpen(false);
    scrollToHash(href);
  };

  return (
    <header
      data-testid="header-navigation"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "border-b border-white/10 bg-[#0B132B]/80 backdrop-blur-xl"
          : "border-b border-transparent bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between gap-4 px-5 sm:px-8">
        <Link
          to="/"
          data-testid="header-logo"
          className="flex items-center rounded-xl bg-white px-3 py-1.5 shadow-lg shadow-black/20 transition-transform duration-300 hover:scale-[1.03]"
        >
          <img src="/hf-logo-no-payoff.png" alt="Human Factor" className="h-8 w-auto sm:h-9" />
        </Link>

        <nav className="hidden items-center gap-1 xl:flex">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={l.testid}
              onClick={(e) => go(e, l.href)}
              className="group relative rounded-lg px-3.5 py-2 text-sm font-medium text-slate-300 transition-colors hover:text-white"
            >
              {l.label}
              <span className="absolute inset-x-3.5 -bottom-0.5 h-px origin-left scale-x-0 bg-gradient-to-r from-[#1B6EF3] to-[#19D6E8] transition-transform duration-300 group-hover:scale-x-100" />
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href="https://app.hfweb.it"
            data-testid="header-btn-accedi"
            className="hidden rounded-full border border-white/20 px-5 py-2.5 text-sm font-semibold text-white transition-all duration-300 hover:border-cyan-300/60 hover:bg-white/5 sm:inline-flex"
          >
            Accedi
          </a>
          <a
            href="#contatti"
            data-testid="header-btn-parla-con-noi"
            onClick={(e) => go(e, "#contatti")}
            className="group inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#1B6EF3] to-[#19D6E8] px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-blue-500/25 transition-all duration-300 hover:scale-[1.03] hover:shadow-cyan-400/40"
          >
            Parla con noi
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
          </a>
          <button
            data-testid="mobile-menu-button"
            aria-label="Apri menu"
            onClick={() => setOpen((v) => !v)}
            className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-white/15 bg-white/5 text-white xl:hidden"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.25 }}
            className="border-t border-white/10 bg-[#0B132B]/95 backdrop-blur-xl xl:hidden"
          >
            <nav className="mx-auto flex max-w-7xl flex-col gap-1 px-5 py-4">
              {LINKS.map((l, i) => (
                <motion.a
                  key={l.href}
                  href={l.href}
                  data-testid={`${l.testid}-mobile`}
                  onClick={(e) => go(e, l.href)}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i, duration: 0.3 }}
                  className="rounded-lg px-3 py-3 font-display text-lg font-semibold text-slate-200 hover:bg-white/5 hover:text-white"
                >
                  {l.label}
                </motion.a>
              ))}
              <a
                href="https://app.hfweb.it"
                data-testid="header-btn-accedi-mobile"
                onClick={() => setOpen(false)}
                className="mt-2 rounded-xl border border-white/20 px-4 py-3 text-center font-semibold text-white"
              >
                Accedi
              </a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
